import { useState, useEffect, useRef, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronLeft, ChevronRight, Plus, CalendarIcon, Search, Upload } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import {
  format,
  addMonths,
  subMonths,
  startOfToday,
} from "date-fns"
import { ru } from "date-fns/locale"
import { MonthCalendar, ThreeMonthsView, YearView } from "@/components/calendar"
import { TodayDashboardWrapper } from "@/components/calendar/TodayDashboardWrapper"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useCalendarEventStore } from "@/store/calendarEventStore"
import { useAuthStore } from "@/store/authStore"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "@/hooks/use-toast"

// Импорты для заметок и напоминаний
import { usePersonalNotesStore } from "@/store/personalNotesStore"
import { usePersonalRemindersStore } from "@/store/personalRemindersStore"
import { PersonalNotesDashboard } from "@/components/calendar/PersonalNotesDashboard"
import { TodayReminders } from "@/components/calendar/TodayReminders"

export default function CalendarPage() {
  const today = startOfToday()

  const { events, addEvent, deleteEvent, init: initEvents, cleanup: cleanupEvents, syncRegionMilestones } = useCalendarEventStore()
  const { user } = useAuthStore()

  const { init: initNotes, cleanup: cleanupNotes } = usePersonalNotesStore()
  const { init: initReminders, cleanup: cleanupReminders, reminders } = usePersonalRemindersStore()

  const [currentMonth, setCurrentMonth] = useState(today)
  const [viewLevel, setViewLevel] = useState<1 | 3 | 12>(1)

  const [filterType, setFilterType] = useState<
    "all" | "task" | "holiday" | "meeting" | "local-emergency" | "leader_birthday" | "leader_appointment" | "branch_opening"
  >("all")

  const [newTitle, setNewTitle] = useState("")
  const [newType, setNewType] = useState<"task" | "holiday" | "meeting" | "local-emergency">("task")
  const [newDesc, setNewDesc] = useState("")
  const [newEventDate, setNewEventDate] = useState<Date | undefined>(today)
  const [openModal, setOpenModal] = useState(false)

  const [selectedDateForNotes, setSelectedDateForNotes] = useState<string>(format(today, "yyyy-MM-dd"))
  const [searchQuery, setSearchQuery] = useState("")

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Фильтруем напоминания ТОЛЬКО текущего пользователя
  const userReminders = useMemo(() => {
    if (!user?.id) return []
    return reminders.filter((r: any) => String(r.userId) === String(user.id))
  }, [reminders, user])

  const filteredEvents = useMemo(() => {
    if (searchQuery.length < 2) return []

    const q = searchQuery.toLowerCase().trim()
    return events.filter((ev) => {
      const titleMatch = ev.title.toLowerCase().includes(q)
      const descMatch = ev.description ? ev.description.toLowerCase().includes(q) : false
      return titleMatch || descMatch
    })
  }, [events, searchQuery])

  const calendarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!user) return

    initEvents()
    initNotes()
    initReminders()
    syncRegionMilestones()

    return () => {
      cleanupEvents()
      cleanupNotes()
      cleanupReminders()
    }
  }, [user, initEvents, cleanupEvents, initNotes, cleanupNotes, initReminders, cleanupReminders, syncRegionMilestones])

  useEffect(() => {
    if (calendarRef.current) {
      const todayElement = calendarRef.current.querySelector('[data-today="true"]')
      if (todayElement) {
        todayElement.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }, [viewLevel, currentMonth])

  const handleDayClick = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd")
    setNewEventDate(date)
    setSelectedDateForNotes(dateStr)
    setOpenModal(true)
  }

  const handleAddEvent = async () => {
    if (!newEventDate || !newTitle.trim() || !user) return

    const dateStr = format(newEventDate, "yyyy-MM-dd")

    await addEvent({
      date: dateStr,
      title: newTitle.trim(),
      type: newType,
      description: newDesc.trim() || undefined,
      createdBy: user.id as number,
    })

    setNewTitle("")
    setNewType("task")
    setNewDesc("")
    setNewEventDate(today)
    setOpenModal(false)
  }

  // ==================== ЗАГРУЗКА EXCEL ====================
  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast({
        title: "Ошибка формата",
        description: "Пожалуйста, загрузите файл Excel (.xlsx или .xls)",
        variant: "destructive"
      })
      return
    }

    try {
      const XLSX = await import('xlsx')
      const data = await file.arrayBuffer()
      const workbook = XLSX.read(data, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet)

      let addedCount = 0

      for (const row of jsonData) {
        const rawDate = row['Дата'] || row['date'] || row['Date']
        const title = row['Название'] || row['title'] || row['Title'] || row['Праздник'] || row['Название события']
        const description = row['Описание'] || row['description'] || row['Description'] || row['Комментарий'] || ""

        if (!rawDate || !title) continue

        let eventDate: Date | null = null
        const dateStr = String(rawDate).trim()

        const ddmmyyyyMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
        if (ddmmyyyyMatch) {
          const [, day, month, year] = ddmmyyyyMatch
          eventDate = new Date(Number(year), Number(month) - 1, Number(day))
        } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          eventDate = parseISO(dateStr)
        } else if (typeof rawDate === 'number') {
          try {
            const excelDate = XLSX.SSF.parse_date_code(rawDate)
            eventDate = new Date(excelDate.y, excelDate.m - 1, excelDate.d)
          } catch {}
        } else {
          eventDate = new Date(dateStr)
        }

        if (!eventDate || isNaN(eventDate.getTime())) continue

        const formattedDate = format(eventDate, "yyyy-MM-dd")

        await addEvent({
          date: formattedDate,
          title: String(title).trim(),
          type: "holiday",
          description: description ? String(description).trim() : undefined,
          createdBy: user?.id as number,
        })

        addedCount++
      }

      if (addedCount > 0) {
        toast({
          title: "✅ Успешно",
          description: `Загружено ${addedCount} праздников из Excel`,
        })
      } else {
        toast({
          title: "Предупреждение",
          description: "В файле не найдено корректных данных",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error("Ошибка при обработке Excel:", error)
      toast({
        title: "Ошибка загрузки",
        description: "Не удалось прочитать файл. Проверьте формат таблицы.",
        variant: "destructive"
      })
    }

    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const dayEvents = newEventDate
    ? events.filter((ev) => ev.date === format(newEventDate, "yyyy-MM-dd"))
    : []

  const toggleView = () => {
    setViewLevel((prev) => (prev === 1 ? 3 : prev === 3 ? 12 : 1))
  }

  const goToPrevMonth = () => setCurrentMonth((prev) => subMonths(prev, 1))
  const goToNextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1))
  const goToToday = () => setCurrentMonth(today)

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case "task": return "Задача"
      case "holiday": return "Праздник"
      case "meeting": return "Встреча"
      case "local-emergency": return "Локальное ЧС"
      case "leader_birthday": return "🎂 День рождения руководителя"
      case "leader_appointment": return "📅 Годовщина назначения"
      case "branch_opening": return "🏛️ Годовщина открытия"
      default: return type
    }
  }

  return (
    <div className="p-6 md:p-10 min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950">
      {/* Шапка страницы */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 md:mb-10 gap-4 sm:gap-0">
        <h1 className="text-4xl md:text-5xl font-bold text-white bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
          Календарь ОРВ ВСКС
        </h1>

        <div className="flex flex-wrap gap-3 items-center">
          <Button
            onClick={goToToday}
            variant="outline"
            className="bg-gradient-to-r from-blue-600/70 to-cyan-600/70 hover:from-blue-700 hover:to-cyan-700 text-white border-blue-500/50"
          >
            <CalendarIcon className="mr-2 h-5 w-5" />
            Сегодня
          </Button>

          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg flex items-center gap-2"
          >
            <Upload className="h-5 w-5" />
            Загрузить праздники из Excel
          </Button>

          <Button
            onClick={() => {
              setNewEventDate(today)
              setSelectedDateForNotes(format(today, "yyyy-MM-dd"))
              setOpenModal(true)
            }}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg"
          >
            <Plus className="mr-2 h-5 w-5" />
            Добавить событие
          </Button>

          <Button
            onClick={toggleView}
            variant="outline"
            className="bg-gradient-to-r from-purple-600/70 to-pink-600/70 hover:from-purple-700 hover:to-pink-700 text-white border-purple-500/50 min-w-[220px]"
          >
            {viewLevel === 1
              ? "Показать три месяца"
              : viewLevel === 3
              ? "Показать весь год"
              : "Вернуться к одному месяцу"}
            <motion.div
              animate={{ rotate: viewLevel === 12 ? 180 : 0 }}
              transition={{ duration: 0.4 }}
              className="ml-2"
            >
              <ChevronDown className="h-5 w-5" />
            </motion.div>
          </Button>
        </div>
      </div>

      {/* Скрытый input для файла */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleExcelUpload}
        className="hidden"
      />

      {/* Фильтры */}
      <div className="mb-6 flex justify-center md:justify-start">
        <Tabs value={filterType} onValueChange={(v) => setFilterType(v as typeof filterType)}>
          <TabsList className="bg-purple-950/70 border border-purple-700/50">
            <TabsTrigger value="all">Все</TabsTrigger>
            <TabsTrigger value="task">Дела</TabsTrigger>
            <TabsTrigger value="holiday">Праздники</TabsTrigger>
            <TabsTrigger value="meeting">Встречи</TabsTrigger>
            <TabsTrigger value="local-emergency">Локальное ЧС</TabsTrigger>
            <TabsTrigger value="leader_birthday">🎂 Дни рождения</TabsTrigger>
            <TabsTrigger value="leader_appointment">📅 Назначения</TabsTrigger>
            <TabsTrigger value="branch_opening">🏛️ Открытия</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Поиск */}
      <div className="mb-6">
        <Input
          type="search"
          placeholder="Поиск по названию или описанию события..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md bg-indigo-950/60 border-purple-600 text-white placeholder:text-purple-400 focus:ring-purple-500 focus:border-purple-500 rounded-xl h-11 shadow-sm"
        />
      </div>

      {/* Результаты поиска */}
      {searchQuery.trim().length >= 2 && (
        <div className="mb-8 bg-gradient-to-br from-purple-950/60 to-indigo-950/60 rounded-2xl border border-purple-700/40 p-5 shadow-xl backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-purple-200 mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-purple-400" />
            Результаты по запросу: «{searchQuery.trim()}»
          </h3>

          {filteredEvents.length === 0 ? (
            <div className="text-center py-10 text-purple-300/70 italic text-base">
              Ничего не найдено
            </div>
          ) : (
            <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2">
              {filteredEvents.map((ev) => (
                <div
                  key={ev.id}
                  className="flex items-start justify-between bg-purple-900/30 p-4 rounded-xl border border-purple-600/30 hover:bg-purple-800/40 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", 
                        ev.type === "task" ? "bg-gradient-to-r from-red-500 to-rose-600" :
                        ev.type === "holiday" ? "bg-gradient-to-r from-emerald-500 to-green-600" :
                        ev.type === "meeting" ? "bg-gradient-to-r from-blue-500 to-cyan-600" :
                        ev.type === "local-emergency" ? "bg-gradient-to-r from-orange-500 to-amber-600" :
                        ev.type === "leader_birthday" ? "bg-gradient-to-r from-pink-500 to-rose-600" :
                        ev.type === "leader_appointment" ? "bg-gradient-to-r from-amber-500 to-yellow-600" :
                        "bg-gradient-to-r from-emerald-500 to-teal-600"
                      )} />
                      <p className="font-medium text-white text-base">{ev.title}</p>
                    </div>

                    <p className="text-sm text-purple-300">
                      {format(parseISO(ev.date), "d MMMM yyyy", { locale: ru })} · {getEventTypeLabel(ev.type)}
                    </p>

                    {ev.description && (
                      <p className="text-sm text-purple-400 mt-1 line-clamp-2">{ev.description}</p>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteEvent(ev.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/30 ml-4 flex-shrink-0"
                  >
                    Удалить
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Навигация по месяцам */}
      {viewLevel === 1 && (
        <div className="flex justify-between items-center mb-6">
          <Button variant="ghost" onClick={goToPrevMonth} className="text-purple-300 hover:text-white">
            <ChevronLeft className="h-8 w-8" />
          </Button>
          <h2 className="text-3xl font-bold text-white">
            {format(currentMonth, "LLLL yyyy", { locale: ru })}
          </h2>
          <Button variant="ghost" onClick={goToNextMonth} className="text-purple-300 hover:text-white">
            <ChevronRight className="h-8 w-8" />
          </Button>
        </div>
      )}

      {/* Основной контент */}
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start">
        <div className="flex-1 w-full max-w-4xl lg:max-w-none mx-auto lg:mx-0 lg:ml-8 xl:ml-12" ref={calendarRef}>
          <AnimatePresence mode="wait">
            <motion.div
              key={`${viewLevel}-${currentMonth.getTime()}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4 }}
              className="w-full"
            >
              {viewLevel === 1 && (
                <MonthCalendar
                  date={currentMonth}
                  isSolo={true}
                  onDayClick={handleDayClick}
                  filterType={filterType}
                />
              )}
              {viewLevel === 3 && (
                <ThreeMonthsView
                  currentDate={currentMonth}
                  onDayClick={handleDayClick}
                  filterType={filterType}
                />
              )}
              {viewLevel === 12 && (
                <YearView
                  currentDate={currentMonth}
                  onDayClick={handleDayClick}
                  filterType={filterType}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="w-full lg:flex-1 flex flex-col lg:flex-row gap-8 lg:gap-10 lg:items-start">
          <div className="w-full lg:flex-1 space-y-6 lg:space-y-8 lg:min-w-[420px] mx-auto lg:mx-0">
            <TodayDashboardWrapper viewLevel={viewLevel} />
            {/* Важно: передаём только личные напоминания */}
            <TodayReminders 
              selectedDate={selectedDateForNotes} 
              reminders={userReminders} 
            />
          </div>

          <div className="w-full lg:flex-1 lg:min-w-[420px] mx-auto lg:mx-0">
            <PersonalNotesDashboard />
          </div>
        </div>
      </div>

      {/* Модальное окно */}
      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="bg-gradient-to-br from-purple-950 to-indigo-950 border-purple-700/50 text-white 
                                  max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col p-6 md:p-8">
          
          <DialogHeader className="flex-shrink-0 pb-5">
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
              {newEventDate ? format(newEventDate, "d MMMM yyyy", { locale: ru }) : "Новое событие"}
            </DialogTitle>
          </DialogHeader>

          {dayEvents.length > 0 && (
            <div className="flex-1 overflow-y-auto pr-3 custom-scrollbar space-y-4">
              <h4 className="text-lg font-medium text-purple-100 mb-3">События на этот день:</h4>
              {dayEvents.map((ev) => (
                <div
                  key={ev.id}
                  className="bg-purple-900/50 p-5 rounded-2xl border border-purple-600/50 hover:border-purple-500 transition-all"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-semibold text-white leading-tight break-words">
                        {ev.title}
                      </p>
                      <p className="text-purple-300 mt-1.5 text-base">
                        {getEventTypeLabel(ev.type)}
                      </p>
                      {ev.description && (
                        <p className="text-purple-200 mt-3 text-[15.5px] leading-relaxed break-words">
                          {ev.description}
                        </p>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteEvent(ev.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/40 text-sm px-4 py-2 flex-shrink-0"
                    >
                      Удалить
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex-shrink-0 mt-8 space-y-6 border-t border-purple-700/50 pt-8">
            <div>
              <Label className="text-purple-200 text-base">Дата события</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-indigo-950/70 border-purple-600 text-white text-base h-12 mt-2"
                  >
                    {newEventDate ? format(newEventDate, "PPP", { locale: ru }) : <span>Выберите дату</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-purple-950 border-purple-700">
                  <Calendar
                    mode="single"
                    selected={newEventDate}
                    onSelect={setNewEventDate}
                    initialFocus
                    className="rounded-md border border-purple-700"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-purple-200 text-base">Название события</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="bg-indigo-950/70 border-purple-600 text-white placeholder:text-purple-400 focus:ring-purple-500 h-12 text-base mt-2"
                placeholder="Например: День рождения коллеги"
              />
            </div>

            <div>
              <Label className="text-purple-200 text-base">Тип</Label>
              <Select value={newType} onValueChange={(v: "task" | "holiday" | "meeting" | "local-emergency") => setNewType(v)}>
                <SelectTrigger className="bg-indigo-950/70 border-purple-600 text-white h-12 text-base mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-purple-950 border-purple-700 text-white">
                  <SelectItem value="task">Задача</SelectItem>
                  <SelectItem value="holiday">Праздник</SelectItem>
                  <SelectItem value="meeting">Встреча</SelectItem>
                  <SelectItem value="local-emergency">Локальное ЧС</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-purple-200 text-base">Описание (опционально)</Label>
              <Textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                className="bg-indigo-950/70 border-purple-600 text-white min-h-[100px] text-base mt-2"
                placeholder="Подробности события..."
              />
            </div>

            <Button
              onClick={handleAddEvent}
              disabled={!newTitle.trim() || !newEventDate}
              className="w-full h-14 text-lg font-medium bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
            >
              Добавить событие
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}